// create-indent.component.ts
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';

import { ReactiveFormsModule } from '@angular/forms';
import { IndentListService } from '../indent-list.service';
import { ToastrService } from 'ngx-toastr';
import { InventoryService } from '../../inventory/inventory.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ProductsFilterComponent } from '../../Common/products-filter/products-filter.component';
import { Router } from '@angular/router';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';

@Component({
  selector: 'app-create-indent',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './create-indent.component.html',
  styleUrls: ['./create-indent.component.scss'],
})
export class CreateIndentComponent implements OnInit {
  indentForm!: FormGroup;
  isSubmitting = false;
  locations: any[] = [];

  private modalService = inject(NgbModal);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private indentService = inject(IndentListService);
  private toastr = inject(ToastrService);
  private locationService = inject(InventoryService);
  private confirmDialog = inject(ConfirmDialogService);

  ngOnInit(): void {
    this.initForm();
    this.getAllStores();
  }

  private initForm() {
    this.indentForm = this.fb.group({
      stores: this.fb.array([]) // will contain FormGroups: { storeId, storeName, items: FormArray[...] }
    });
  }

  get storesFA(): FormArray {
    return this.indentForm.get('stores') as FormArray;
  }

  private createStoreGroup(storeId: string, storeName: string, items: any[]): FormGroup {
    const itemsFA = this.fb.array(
      items.map(item =>
        this.fb.group({
          variantId: [item.variantId],
          productId: [item.productId],
          sku: [item.sku],
          name: [item.name],
          qty: [item.qty, [Validators.required, Validators.min(0)]],
          uom: [item.uom],
          mrp: [item.mrp],
          sellingPrice: [item.sellingPrice]
        })
      )
    );

    return this.fb.group({
      storeId: [storeId],
      storeName: [storeName],
      items: itemsFA
    });
  }

  private clearStoresFormArray() {
    while (this.storesFA.length) {
      this.storesFA.removeAt(0);
    }
  }

  getAllStores() {
    this.locationService.getAllStores().subscribe({
      next: (data: any) => {
        this.locations = data || [];
      },
      error: () => {
        this.toastr.error('Failed to load stores');
      },
    });
  }

  openFilter() {
    const modalRef = this.modalService.open(ProductsFilterComponent, {
      size: 'xl',
      backdrop: 'static',
      keyboard: false,
      scrollable: true,
      centered: true
    });

    modalRef.result
      .then((res) => {
        if (res) {
          this.indentService.getFilteredIndentList(res).subscribe({
            next: (data: any) => {
              this.populateStoresFromProducts(data?.items || []);
              this.toastr.success('Filter applied successfully', 'Success');
            },
            error: (err) => {
              console.error('Error applying filter:', err);
              this.toastr.error('Failed to apply filter', 'Error');
            },
          });
        }
      })
      .catch(() => {});
  }

  populateStoresFromProducts(items: any[]) {
    const storeMap = new Map<string, any>();

    for (const variant of items) {
      const variantBase = {
        variantId: variant.variantId,
        productId: variant.productId,
        sku: variant.sku,
        name: variant.name,
        uom: variant.uom,
        mrp: variant.mrp,
        sellingPrice: variant.sellingPrice
      };

      if (Array.isArray(variant.stores)) {
        for (const s of variant.stores) {
          const sid = s.storeId;
          const qty = s.qty ?? 0;
          if (!storeMap.has(sid)) {
            const storeInfo = this.locations.find(l => l.locationId === sid) || this.locations.find(l => l.id === sid) || null;
            const storeName = storeInfo ? (storeInfo.name || storeInfo.storeName || storeInfo.locationName) : sid;
            storeMap.set(sid, {
              storeId: sid,
              storeName,
              items: []
            });
          }
          storeMap.get(sid).items.push({ ...variantBase, qty });
        }
      }
    }

    this.clearStoresFormArray();

    for (const [storeId, storeData] of storeMap.entries()) {
      this.storesFA.push(this.createStoreGroup(storeId, storeData.storeName, storeData.items));
    }
  }

  itemsFA(storeIndex: number): FormArray {
    return this.storesFA.at(storeIndex).get('items') as FormArray;
  }

  // <-- fixed: cast AbstractControl to FormGroup inside map so TS knows controls/value shape -->
  buildRequestBody() {
    const storesPayload = this.storesFA.controls.map((ctrl) => {
      const storeGroup = ctrl as FormGroup;
      const sg = storeGroup.value as {
        storeId: string;
        storeName?: string;
        items: Array<{ variantId: string; qty: number }>;
      };

      return {
        storeId: sg.storeId,
        items: (sg.items || []).map(it => ({
          variantId: it.variantId,
          qty: Number(it.qty) || 0
        }))
      };
    });

    return {
        stores: storesPayload
    };
  }

  async onSubmit() {
    if (this.indentForm.invalid) {
      this.toastr.error('Please fix validation errors before submitting');
      this.indentForm.markAllAsTouched();
      return;
    }

    const body = this.buildRequestBody();

    const storeCount = body.stores.length;
    const itemCount = body.stores.reduce(
      (sum, s) => sum + (s.items?.length || 0),
      0,
    );
    const proceed = await this.confirmDialog.confirm({
      title: 'Submit Indent?',
      message: `Are you sure you want to create indents for <strong>${storeCount}</strong> store(s) covering <strong>${itemCount}</strong> item(s)? Once submitted they are available for PO raising.`,
      confirmText: 'Submit',
      cancelText: 'Cancel',
      size: 'lg',
    });
    if (!proceed) return;

    this.indentService.createIndent(body).subscribe({
      next: (res) => {
        this.toastr.success('Indent created successfully', 'Success');
        this.router.navigate(['/indent-list']);
        this.indentForm.reset();
        this.clearStoresFormArray();
      },
      error: (err) => {
        console.error('Error creating indent:', err);
        this.toastr.error('Failed to create indent', 'Error');
      },
    });
  }

  // add inside CreateIndentComponent class
// add inside CreateIndentComponent class
get stores(): FormArray {
  return this.indentForm.get('stores') as FormArray;
}

getItems(storeIndex: number): FormArray {
  return this.stores.at(storeIndex).get('items') as FormArray;
}


}
